import React, { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useParams } from "react-router-dom";
import { Accordion, AccordionTab } from "primereact/accordion";

import "../../common/game.css";
import appStore from "../../storage/AppStore";
import clubStore from "../../storage/ClubStore";
import systemStore from "../../storage/SystemStore";
import Btn from "../../ui/Btn";
import PageLayout from "../PageLayout";
import { HtmlDiv } from "../../common/utils";

const CompetitionDetails = ({ role = "MGR" }) => {
  const { id } = useParams();
  const nav = useNavigate();

  const basePath = role === "TRN" ? "/trainer" : "/manager";
  const item = clubStore.tournament.competition_details;

  useEffect(() => {
    clubStore.tournament.setProperty('competition_details', null);
    if (id) {
      clubStore.tournament.getCompetitionDetails(id).catch((err) => {
        systemStore.setError(err?.message || appStore.tr.not_found);
      });
    }
  }, [id]);

  const canRespondInvitation =
    ["MGR", "TRN"].includes(role) &&
    item?.is_invited &&
    item?.invite_status === "invited";

  const onAcceptInvitation = async () => {
    try {
      const ret = await clubStore.tournament.acceptTournamentInvitation(id, clubStore.club.id);
      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.invitation_accepted);
        await clubStore.tournament.getCompetitionDetails(id);
      }
    } catch (err) {
      systemStore.setError(err?.message || appStore.tr.error);
    }
  };

  const onDenyInvitation = async () => {
    try {
      const ret = await clubStore.tournament.declineTournamentInvitation(id, clubStore.club.id);
      if (ret?.result === 0) {
        systemStore.setSuccess(appStore.tr.invitation_declined);
        await clubStore.tournament.getCompetitionDetails(id);
      }
    } catch (err) {
      systemStore.setError(err?.message || appStore.tr.error);
    }
  };

  const renderInviteStatusBadge = () => {
    if (!item?.is_invited) return null;
    if (item.invite_status === "accepted") {
      return <span className="badge badge-success">{appStore.tr.accepted || "Accepted"}</span>;
    }
    if (item.invite_status === "deleted" || item.invite_status === "declined") {
      return <span className="badge badge-danger">{appStore.tr.declined || "Declined"}</span>;
    }
    return <span className="badge badge-warning">{appStore.tr.invited || "Invited"}</span>;
  };

  const renderTeams = () => {
    const teams = item?.teams || [];
    if (!teams.length) {
      return <div className="competition-details__empty">{appStore.tr.teams_not_selected || "Teams Not Selected"}</div>;
    }
    return (
      <div className="competition-details__team-list">
        {teams.map((team) => (
          <span key={team.id} className="competition-details__team-chip">
            <i className="pi pi-users" aria-hidden="true"/><span>{team.name}</span>
          </span>
        ))}
      </div>
    );
  };

  const renderCompanies = () => {
    const companies = item?.companies || [];
    if (!companies.length) {
      return <div className="competition-details__empty">{appStore.tr.not_found}</div>;
    }
    return (
      <div className="competition-details__companies">
        {companies.map((company) => {
          let badgeClass = "badge badge-warning";
          let badgeText = appStore.tr.invited;
          if (company.invite_status === "accepted") {
            badgeClass = "badge badge-success";
            badgeText = appStore.tr.accepted;
          } else if (company.invite_status === "deleted" || company.invite_status === "declined") {
            badgeClass = "badge badge-danger";
            badgeText = appStore.tr.declined;
          } else if (company.invite_status === "organizer") {
            badgeClass = "badge badge-success";
            badgeText = appStore.tr.organizer;
          }
          return (
            <div key={company.id || company.company_id} className="competition-details__company">
              <div className="competition-details__company-main">
                <div className="competition-details__company-logo">
                  {company.logo ? (
                    <img src={company.logo} alt={company.name}/>
                  ) : (
                    <i className="pi pi-building" aria-hidden="true"/>
                  )}
                </div>
                <div className="competition-details__company-info">
                  <div className="competition-details__company-name">{company.name}</div>
                  {company.country_text && <div className="competition-details__company-country">{company.country_text}</div>}
                </div>
              </div>
              <span className={badgeClass}>{badgeText}</span>
            </div>
          );
        })}
      </div>
    );
  };

  const sexText = (sex) => {
    if (sex === 'M') return appStore.tr.male;
    if (sex === 'F') return appStore.tr.female;
    return appStore.tr.mixed;
  };

  const ageText = (d) => {
    return d.age_from != null || d.age_to != null
      ? `${d.age_from ?? '-'} - ${d.age_to ?? '-'}`
      : null;
  };

  const renderDivisions = () => (
    <div className="competition-details__divisions">
      {item.divisions.map((d, index) => (
        <div key={index} className="competition-details__division">
          <div className="competition-details__division-name">{d.name}</div>
          <div className="competition-details__division-meta">
            {(d.age_from != null || d.age_to != null) && (
              <span><i className="pi pi-calendar" aria-hidden="true"/>{appStore.tr.age}: {ageText(d)}</span>
            )}
            {d.sex && (
              <span><i className="pi pi-users" aria-hidden="true"/>{appStore.tr.sex}: {sexText(d.sex)}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );

  if (!item) {
    return (
      <PageLayout title={appStore.tr.competition || appStore.tr.tournament} showBack>
        <div className="competition-details__loading">
          <i className="pi pi-spin pi-spinner" aria-hidden="true"/>
          <span>{appStore.tr.loading || "Loading..."}</span>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout header={appStore.tr.tournament}>
      <div className="competition-details">
        <section className={`competition-details__hero ${item.is_invited ? "competition-card-invited" : "competition-card-own"}`}>
          <div className="competition-details__hero-heading">
            <div className="competition-details__hero-icon" aria-hidden="true"><i className="pi pi-trophy"/></div>
            <div className="competition-details__hero-content">
              <h2 className="competition-details__title">{item.name}</h2>
              <div className="competition-details__hero-subtitle">
                <i className="pi pi-building" aria-hidden="true"/>
                <span>{item.company?.name || "-"}{item.country_text ? ` · ${item.country_text}` : ""}</span>
              </div>
            </div>
          </div>
          <div className="competition-details__statuses">
            {item.is_owner && (
              <span className="badge badge-success">{appStore.tr.organizer || "Organizer"}</span>
            )}
            {renderInviteStatusBadge()}
          </div>
        </section>

        <section className="competition-details__panel">
          <div className="competition-details__section-heading">
            <span className="competition-details__section-icon"><i className="pi pi-info-circle" aria-hidden="true"/></span>
            <h3>{appStore.tr.tournament}</h3>
          </div>
          <div className="competition-details__facts">
            <div className="competition-details__fact">
              <i className="pi pi-building" aria-hidden="true"/>
              <div className="competition-details__fact-content">
                <span className="competition-details__fact-label">{appStore.tr.organizer || "Organizer"}</span>
                <span className="competition-details__fact-value">{item.company?.name || "-"}{item.country_text ? `, ${item.country_text}` : ""}</span>
              </div>
            </div>
            <div className="competition-details__fact">
              <i className="pi pi-calendar" aria-hidden="true"/>
              <div className="competition-details__fact-content">
                <span className="competition-details__fact-label">{appStore.tr.beginning || "Beginning"}</span>
                <span className="competition-details__fact-value">{item.start_date || "-"}</span>
              </div>
            </div>
            <div className="competition-details__fact">
              <i className="pi pi-calendar" aria-hidden="true"/>
              <div className="competition-details__fact-content">
                <span className="competition-details__fact-label">{appStore.tr.end}</span>
                <span className="competition-details__fact-value">{item.end_date || "-"}</span>
              </div>
            </div>
            <div className="competition-details__fact">
              <i className="pi pi-dollar" aria-hidden="true"/>
              <div className="competition-details__fact-content">
                <span className="competition-details__fact-label">{appStore.tr.fee}</span>
                <span className="competition-details__fact-value">{item.fee || 0}</span>
              </div>
            </div>
            {item.is_invited && (
              <div className="competition-details__fact">
                <i className="pi pi-envelope" aria-hidden="true"/>
                <div className="competition-details__fact-content">
                  <span className="competition-details__fact-label">{appStore.tr.invitation}</span>
                  <span className="competition-details__fact-value">{renderInviteStatusBadge()}</span>
                </div>
              </div>
            )}
            {item.address && (
              <div className="competition-details__fact competition-details__fact--wide">
                <i className="pi pi-map-marker" aria-hidden="true"/>
                <div className="competition-details__fact-content">
                  <span className="competition-details__fact-label">{appStore.tr.address || "Address"}</span>
                  <span className="competition-details__fact-value">{item.address}</span>
                </div>
              </div>
            )}
          </div>
        </section>

        {(item.invite_status === 'accepted' || item.invite_status === 'organizer') && (
          <section className="competition-details__panel">
            <div className="competition-details__section-heading">
              <span className="competition-details__section-icon"><i className="pi pi-file" aria-hidden="true"/></span>
              <h3>{appStore.tr.regulations}</h3>
            </div>
            {item.pdf && (
              <div className="competition-details__file-action">
                <Btn icon="pi pi-file-pdf" label={appStore.tr.regulations} severity="success" onClick={() => window.open(item.pdf, "_blank")}/>
              </div>
            )}
            <div className="competition-details__accordion">
              <Accordion>
                <AccordionTab header={appStore.tr.regulations}>
                  {item.regulations ? <HtmlDiv content={item.regulations}/> : <span>{appStore.tr.no_regulations}</span>}
                </AccordionTab>
              </Accordion>
            </div>
          </section>
        )}

        {item.divisions && (
          <section className="competition-details__panel">
            <div className="competition-details__section-heading">
              <span className="competition-details__section-icon"><i className="pi pi-th-large" aria-hidden="true"/></span>
              <h3>{appStore.tr.divisions}</h3>
              <span className="competition-details__count">{item.divisions.length}</span>
            </div>
            {renderDivisions()}
          </section>
        )}

        {canRespondInvitation && (
          <section className="competition-details__panel competition-details__panel--invitation">
            <div className="competition-details__section-heading">
              <span className="competition-details__section-icon"><i className="pi pi-envelope" aria-hidden="true"/></span>
              <h3>{appStore.tr.tournament_invitation_text}</h3>
            </div>
            <div className="competition-details__accordion">
              <Accordion>
                <AccordionTab header={appStore.tr.regulations}>
                  {item.regulations ? <HtmlDiv content={item.regulations}/> : <span>{appStore.tr.no_regulations}</span>}
                </AccordionTab>
              </Accordion>
            </div>
            <div className="competition-details__actions">
              {item.pdf && (
                <Btn icon="pi pi-file-pdf" label={appStore.tr.regulations} severity="success" onClick={() => window.open(item.pdf, "_blank")}/>
              )}
              <Btn icon="pi pi-check" label={appStore.tr.accept || "Accept"} severity="success" onClick={onAcceptInvitation}/>
              <Btn icon="pi pi-times" label={appStore.tr.deny || "Deny"} severity="danger" onClick={onDenyInvitation}/>
            </div>
          </section>
        )}

        <section className="competition-details__panel">
          <div className="competition-details__section-heading">
            <span className="competition-details__section-icon"><i className="pi pi-users" aria-hidden="true"/></span>
            <h3>{appStore.tr.game_teams || "Game Teams"}</h3>
            <span className="competition-details__count">{(item?.teams || []).length}</span>
          </div>
          {renderTeams()}
        </section>

        <section className="competition-details__panel">
          <div className="competition-details__section-heading">
            <span className="competition-details__section-icon"><i className="pi pi-building" aria-hidden="true"/></span>
            <h3>{appStore.tr.clubs || "Companies"}</h3>
            <span className="competition-details__count">{(item?.companies || []).length}</span>
          </div>
          {renderCompanies()}
        </section>

        <div className="competition-details__footer">
          {item.is_owner && role === "MGR" && (
            <>
              <Btn icon="pi pi-pencil" label={appStore.tr.edit} onClick={() => nav(`${basePath}/tournament/edit/${item.id}`)}/>
              <Btn icon="pi pi-sitemap" label={appStore.tr.clubs} severity="secondary" onClick={() => nav(`${basePath}/tournament/companies/${item.id}`)}/>
            </>
          )}
          {role === "MGR" && item.fee > 0 && item.invite_status === 'accepted' && (
            <Btn icon="pi pi-dollar" label={appStore.tr.billing || "Billing"} severity="secondary" onClick={() => nav(`${basePath}/tournament/billing/${item.id}`)}/>
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default observer(CompetitionDetails);
